/**
 * AI Chat Component for WPS Pages
 * Provides conversational AI interface for page content and design modifications
 */

export function aiChat() {
  return {
    messages: [],
    userInput: '',
    selectedImage: null,
    imagePreview: null,
    isLoading: false,
    error: null,
    pageId: null,
    editor: null,

    init() {
      // Get page ID from data attribute
      const pageIdEl = document.querySelector('[data-page-id]');
      this.pageId = pageIdEl?.dataset.pageId;

      if (!this.pageId) {
        console.error('AI Chat: Page ID not found');
        return;
      }

      // Get editor instance
      this.editor = window.alpineEditors?.['alpineblocks-editor'];

      if (!this.editor) {
        console.warn('AI Chat: Editor instance not found yet, will retry on first message');
      }

      // Load chat history from sessionStorage
      this.loadChatHistory();

      // If no history, show welcome message
      if (this.messages.length === 0) {
        this.messages.push({
          role: 'assistant',
          content: 'Hi! I can help you build and modify your page. Try asking me to add a hero section, implement a design from a screenshot, or apply a template.',
          timestamp: new Date().toISOString()
        });
        this.saveChatHistory();
      }
    },

    loadChatHistory() {
      try {
        const storageKey = `wps_chat_history_${this.pageId}`;
        const stored = sessionStorage.getItem(storageKey);
        if (stored) {
          this.messages = JSON.parse(stored);
        }
      } catch (error) {
        console.error('Failed to load chat history:', error);
      }
    },

    saveChatHistory() {
      try {
        const storageKey = `wps_chat_history_${this.pageId}`;
        // Keep last 50 messages
        const toSave = this.messages.slice(-50);
        sessionStorage.setItem(storageKey, JSON.stringify(toSave));
      } catch (error) {
        console.error('Failed to save chat history:', error);
      }
    },

    getChatHistoryForAPI() {
      // Convert UI messages to API format (just role and content, no operations)
      return this.messages
        .filter(msg => msg.role !== 'error') // Exclude error messages
        .map(msg => ({
          role: msg.role,
          content: msg.content
        }));
    },

    async sendMessage() {
      if ((!this.userInput.trim() && !this.selectedImage) || this.isLoading) {
        return;
      }

      const userMessage = this.userInput.trim() || 'Implement this design';
      const imageData = this.imagePreview; // base64 data

      // Add user message to UI
      this.messages.push({
        role: 'user',
        content: userMessage,
        image: this.imagePreview,
        timestamp: new Date().toISOString()
      });

      // Clear input and image
      this.userInput = '';
      this.selectedImage = null;
      this.imagePreview = null;
      this.error = null;
      this.isLoading = true;

      this.$nextTick(() => this.scrollToBottom());

      try {
        // Retry getting editor if not found initially
        if (!this.editor) {
          this.editor = window.alpineEditors?.['alpineblocks-editor'];
        }

        // Call chat API
        const response = await this.callChatAPI(userMessage, imageData);

        if (response.success) {
          // Add AI message
          this.messages.push({
            role: 'assistant',
            content: response.message,
            timestamp: new Date().toISOString()
          });

          // Save to sessionStorage
          this.saveChatHistory();

          // Apply operations to editor if present
          if (response.operations && response.operations.length > 0) {
            await this.applyOperations(response.operations);
          }

          // Handle template application if present
          if (response.template_applied) {
            await this.applyTemplate(response.template_applied);
          }

          // Handle /clear command
          if (response.history_cleared) {
            this.messages = [{
              role: 'assistant',
              content: 'Chat history cleared. How can I help you?',
              timestamp: new Date().toISOString()
            }];
            this.saveChatHistory();
          }
        } else {
          throw new Error(response.error || 'Unknown error');
        }
      } catch (error) {
        console.error('AI Chat error:', error);
        this.error = error.message;
        this.messages.push({
          role: 'error',
          content: `Error: ${error.message}`,
          timestamp: new Date().toISOString()
        });
        this.saveChatHistory();
      } finally {
        this.isLoading = false;
        this.$nextTick(() => this.scrollToBottom());
      }
    },

    async applyOperations(operations) {
      if (!this.editor) {
        throw new Error('Editor not initialized');
      }

      // Save state before AI modifications (for undo)
      if (this.editor.historyManager) {
        this.editor.historyManager.saveState('AI modification');
      }

      try {
        for (const op of operations) {
          await this.applyOperation(op);
        }

        // Trigger editor update event
        if (this.editor.$dispatch) {
          this.editor.$dispatch('editor-updated', { id: this.editor.id });
        }

        // Manually trigger re-render
        if (typeof this.editor.triggerRedraw === 'function') {
          this.editor.triggerRedraw();
        }
      } catch (error) {
        console.error('Operation failed:', error);
        // Revert changes on error
        if (this.editor.historyManager) {
          this.editor.historyManager.undo();
        }
        throw error;
      }
    },

    async applyOperation(operation) {
      const { type } = operation;

      switch (type) {
        case 'add_block':
          return this.addBlock(operation);
        case 'replace_block':
          return this.replaceBlock(operation);
        case 'update_block':
          return this.updateBlock(operation);
        case 'delete_block':
          return this.deleteBlock(operation);
        case 'reorder_blocks':
          return this.reorderBlocks(operation);
        default:
          throw new Error(`Unknown operation type: ${type}`);
      }
    },

    addBlock(operation) {
      const { block_class, data, position } = operation;

      // Initialize new block
      const block = this.editor.initBlock(block_class, false);
      if (!block) {
        throw new Error(`Failed to create block: ${block_class}`);
      }

      // Set block data/config
      if (block.config) {
        block.config = { ...block.config, ...data };
      } else if (block.data) {
        block.data = { ...block.data, ...data };
      }

      // Determine position
      const pos = position === 'end' ? this.editor.blocks.length : (position || 0);

      // Insert block
      this.editor.blocks.splice(pos, 0, block);
    },

    updateBlock(operation) {
      const { block_id, data } = operation;

      const block = this.editor.blocks.find(b => b.id === block_id);
      if (!block) {
        throw new Error(`Block not found: ${block_id}`);
      }

      // Merge new data with existing config/data
      if (block.config) {
        block.config = { ...block.config, ...data };
      } else if (block.data) {
        block.data = { ...block.data, ...data };
      }

      // Trigger block redraw if available
      if (typeof block.triggerRedraw === 'function') {
        block.triggerRedraw();
      }
    },

    deleteBlock(operation) {
      const { block_id } = operation;

      const index = this.editor.blocks.findIndex(b => b.id === block_id);
      if (index === -1) {
        throw new Error(`Block not found: ${block_id}`);
      }

      this.editor.blocks.splice(index, 1);
    },

    replaceBlock(operation) {
      const { block_id, block_class, data } = operation;

      const index = this.editor.blocks.findIndex(b => b.id === block_id);
      if (index === -1) {
        throw new Error(`Block not found: ${block_id}`);
      }

      // Create new block
      const newBlock = this.editor.initBlock(block_class, false);
      if (!newBlock) {
        throw new Error(`Failed to create block: ${block_class}`);
      }

      // Set block data
      if (newBlock.config) {
        newBlock.config = { ...data };
      } else if (newBlock.data) {
        newBlock.data = { ...data };
      }

      // Replace in array
      this.editor.blocks.splice(index, 1, newBlock);
    },

    reorderBlocks(operation) {
      const { from, to } = operation;

      if (from < 0 || from >= this.editor.blocks.length || to < 0 || to >= this.editor.blocks.length) {
        throw new Error(`Invalid reorder positions: from ${from} to ${to}`);
      }

      const block = this.editor.blocks.splice(from, 1)[0];
      this.editor.blocks.splice(to, 0, block);
    },

    async applyTemplate(templateId) {
      // TODO: Implement template application
      // This will fetch template via API and parse its blocks
      console.log('Template application:', templateId);
      this.messages.push({
        role: 'system',
        content: `Template "${templateId}" suggested, but template application is not yet implemented.`,
        timestamp: new Date().toISOString()
      });
    },

    handleImageSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        this.error = 'Please select a valid image file (PNG, JPEG, GIF, or WebP)';
        return;
      }

      // Validate file size (10MB max)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        this.error = 'Image file is too large. Maximum size is 10MB.';
        return;
      }

      this.selectedImage = file;
      this.error = null;

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.imagePreview = e.target.result; // base64 data URL
      };
      reader.readAsDataURL(file);
    },

    removeImage() {
      this.selectedImage = null;
      this.imagePreview = null;
      // Reset file input
      const fileInput = this.$refs.imageInput;
      if (fileInput) {
        fileInput.value = '';
      }
    },

    async callChatAPI(message, imageData) {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      const response = await fetch('/admin/pages/ai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          message: message,
          page_id: this.pageId,
          image: imageData,
          chat_history: this.getChatHistoryForAPI()
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    },

    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },

    clearChat() {
      if (confirm('Clear entire chat history? This cannot be undone.')) {
        this.userInput = '/clear';
        this.sendMessage();
      }
    },

    formatMessage(content) {
      // Simple markdown-like formatting
      // Replace **bold** with <strong>
      let formatted = content.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      // Replace *italic* with <em>
      formatted = formatted.replace(/\*(.+?)\*/g, '<em>$1</em>');
      // Replace newlines with <br>
      formatted = formatted.replace(/\n/g, '<br>');
      // Replace code blocks with <code>
      formatted = formatted.replace(/`(.+?)`/g, '<code class="bg-gray-100 px-1 rounded">$1</code>');

      return formatted;
    },

    handleKeydown(event) {
      // Send on Enter (without Shift)
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        this.sendMessage();
      }
    }
  };
}

// Export for global access
if (typeof window !== 'undefined') {
  window.aiChat = aiChat;
}
